import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'authentication/AuthContext';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import * as R from 'ramda';
import PropTypes from 'prop-types';

import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { isLessThan } from 'utils/helpers/formValidations';
import { GT_ZERO, LT_VAL_CHAR, REQUIRED } from 'utils/helpers/validationMessages';

import {
  useGetBookingOriginsQuery,
  useGetLocationsQuery,
  useGetShippersQuery,
} from '../../batchesEndpoints';

const Shipments = ({
  batchOwnerName,
  dataCompanyId,
  locationHelperText,
  originId,
  originName,
  productId,
  setComparisonState,
  setLocationHelperText,
  setShipmentState,
  setValidationMessages,
  shipmentState,
  validationMessages,
}) => {
  const navigate = useNavigate();
  const { bookingOrigin, comments, location, shipper, volume, agreementCode } = shipmentState;
  const { affiliates, currentAffiliate } = useContext(AuthContext);
  const dataCompanies = affiliates.reduce((a, c) => {
    return [c.dataCompanyId, ...a];
  }, []);
  const inventoryOwner = { batchOwnerName, dataCompanyId };
  const isShipperBatchOwner = R.equals(R.prop('shipperName', shipper), batchOwnerName);
  const bookingOriginParams = { inventoryOwner, originId };

  if (!dataCompanies.includes(dataCompanyId)) {
    navigate(`/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}`);
  }
  const {
    data: shippers = [],
    isFetching: isFetchingShippers,
    isSuccess: isSuccessShippers,
  } = useGetShippersQuery({ params: { dataCompanyId } });

  const handleShipperChange = value => {
    setShipmentState(
      R.mergeLeft({
        location: null,
        shipper: value,
      })
    );
    setLocationHelperText('');
  };

  const {
    data: bookingOriginsQuery = [],
    isFetching: isFetchingBookingOrigins,
    isSuccess: isSuccessBookingOrigins,
  } = useGetBookingOriginsQuery({ params: bookingOriginParams });

  const allBookingOrigins = R.prepend(
    { bookingOriginId: originId, bookingOriginName: originName },
    bookingOriginsQuery
  );

  const handleBookingOriginChange = value => {
    setShipmentState(
      R.mergeLeft({
        bookingOrigin: value,
        location: null,
      })
    );
    setLocationHelperText('');
  };

  const locationsParams = {
    bookingOriginId: R.prop('bookingOriginId', bookingOrigin),
    dataCompanyId,
    productId,
  };

  const {
    data: locationsQuery = [],
    isFetching: isFetchingLocations,
    isSuccess: isSuccessLocations,
  } = useGetLocationsQuery({ params: locationsParams });

  const handleLocationChange = value => {
    setShipmentState(R.assoc('location', value));
    if (!value.isDefaultLocation)
      setLocationHelperText(
        'This will create a linked distribution going to the selected location for the shipment.'
      );
    else setLocationHelperText('');
  };

  const setDefaultComparisonLocation = value => setComparisonState(R.assoc('location', value));

  useEffect(() => {
    if (R.and(R.isNil(location), isSuccessLocations)) {
      R.compose(
        R.tap(R.unless(R.isNil, x => setDefaultComparisonLocation(x))),
        R.tap(R.unless(R.isNil, x => handleLocationChange(x))),
        R.find(R.prop('isDefaultLocation'))
      )(locationsQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessLocations, location, locationsQuery]);

  const validateVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [R.gt(1), R.always(GT_ZERO)],
    [R.T, R.always('')],
  ]);

  const handleVolumeChange = value => {
    const sanitized = R.replace(/[^0-9]/g, '', value);
    setShipmentState(R.assoc('volume', formatNumberWithoutDecimals(sanitized)));

    setValidationMessages(R.assoc('volume', validateVolume(sanitized)));
  };

  const handleAgreementCodeChange = R.compose(
    setShipmentState,
    R.assoc('agreementCode'),
    R.path(['target', 'value'])
  );

  const MAX_COMMENT_CHARS = 255;
  const validateComments = R.cond([
    [value => !isLessThan(value, MAX_COMMENT_CHARS), R.always(LT_VAL_CHAR(MAX_COMMENT_CHARS))],
    [R.T, R.always('')],
  ]);

  const handleCommentsChange = value => {
    setShipmentState(R.assoc('comments', value));
    setValidationMessages(R.assoc('comments', validateComments(value)));
  };

  return (
    <>
      <Autocomplete
        disableClearable
        disabled={!isSuccessShippers}
        getOptionLabel={R.prop('shipperName')}
        isOptionEqualToValue={R.eqProps('shipperIdName')}
        loading={isFetchingShippers}
        onChange={(_, value) => handleShipperChange(value)}
        options={shippers}
        renderInput={inputParams => <TextField {...inputParams} label="Shipper" />}
        value={shipper}
      />
      <Autocomplete
        disableClearable
        disabled={!isSuccessBookingOrigins || allBookingOrigins.length === 1}
        getOptionLabel={R.prop('bookingOriginName')}
        isOptionEqualToValue={R.eqProps('bookingOriginId')}
        loading={isFetchingBookingOrigins}
        onChange={(_, value) => handleBookingOriginChange(value)}
        options={allBookingOrigins}
        renderInput={inputParams => <TextField {...inputParams} label="Booking Origin" />}
        value={bookingOrigin}
      />
      <Stack spacing={1}>
        <Autocomplete
          disableClearable
          disabled={!isSuccessLocations || R.not(isShipperBatchOwner) || R.isEmpty(locationsQuery)}
          getOptionLabel={R.prop('locationName')}
          isOptionEqualToValue={R.eqProps('id')}
          loading={isFetchingLocations}
          onChange={(_, value) => handleLocationChange(value)}
          options={locationsQuery}
          renderInput={inputParams => (
            <TextField {...inputParams} helperText={locationHelperText} label="Location" />
          )}
          value={location}
        />
        {R.and(isSuccessLocations, R.isEmpty(locationsQuery)) && (
          <Alert severity="warning">
            There are no valid locations available for selection. Please select a different Booking
            Origin or update the origin or product on the parent batch.
          </Alert>
        )}
        {R.all(R.equals(true), [
          isSuccessLocations,
          R.not(R.isEmpty(locationsQuery)),
          R.isNil(R.find(R.prop('isDefaultLocation'))(locationsQuery)),
          R.not(R.isNil(shipper)),
          R.not(isShipperBatchOwner),
        ]) && (
          <Alert severity="warning">
            A shipment cannot be made for your selected Shipper. Please select yourself as the
            Shipper or a different Booking Origin, or update the origin or product in the parent
            batch.
          </Alert>
        )}
      </Stack>
      <TextField
        error={!R.isEmpty(validationMessages.volume)}
        fullWidth
        helperText={validationMessages.volume}
        label="Volume"
        onChange={event => handleVolumeChange(event.target.value)}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
          },
        }}
        value={volume}
      />
      <TextField
        fullWidth
        helperText="Optional"
        label="Agreement Code"
        onChange={handleAgreementCodeChange}
        value={agreementCode}
      />
      <TextField
        error={!R.isEmpty(validationMessages.comments)}
        helperText={validationMessages.comments || 'Optional'}
        label="Comment"
        multiline
        onChange={event => handleCommentsChange(event.target.value)}
        rows={3}
        slotProps={{
          htmlInput: {
            style: {
              padding: 1,
            },
          },
        }}
        value={comments}
      />
    </>
  );
};

Shipments.propTypes = {
  batchOwnerName: PropTypes.string.isRequired,
  dataCompanyId: PropTypes.number.isRequired,
  locationHelperText: PropTypes.string.isRequired,
  originId: PropTypes.number.isRequired,
  originName: PropTypes.string.isRequired,
  productId: PropTypes.number.isRequired,
  setComparisonState: PropTypes.func.isRequired,
  setLocationHelperText: PropTypes.func.isRequired,
  setShipmentState: PropTypes.func.isRequired,
  setValidationMessages: PropTypes.func.isRequired,
  shipmentState: PropTypes.shape({
    agreementCode: PropTypes.string,
    bookingOrigin: PropTypes.shape({
      bookingOriginId: PropTypes.number,
      bookingOriginName: PropTypes.string,
    }),
    comments: PropTypes.string,
    location: PropTypes.shape({}),
    shipper: PropTypes.shape({}),
    volume: PropTypes.string,
  }).isRequired,
  validationMessages: PropTypes.shape({
    comments: PropTypes.string,
    shipper: PropTypes.string,
    volume: PropTypes.string,
  }).isRequired,
};

export default Shipments;
